import { useState } from 'react';

import {
  useAttachIntakeCandidate,
  useConfirmExecutionCandidate,
  useCreateNewIntakeCandidate,
  useDismissExecutionCandidate,
  useNdaExecutionCandidates,
  useNdaIntakeCandidates,
  useNdaQueuesSummary,
} from '../../hooks/useNda';
import type { NdaExecutionCandidate, NdaIntakeCandidate } from '../../types';

/**
 * NDA decision queue (lifecycle rework PR-4) — a standalone component so the
 * PR-5b docket shell can import it rather than duplicate it. Renders the
 * executed-copy confirm queue: the scanner NEVER auto-captures an executed copy,
 * it queues a candidate here for a human to Confirm (→ capture the executed copy,
 * which drives obligations extraction) or Dismiss. A candidate whose source is
 * mnpi_restricted requires an explicit disclosure confirmation.
 *
 * Scope note: the outbound-bind per-item UI + the Maintenance bulk-bind
 * retirement (build plan PR-4 §4) are deferred to PR-5b, which builds the real
 * docket this component lives in; the bulk-bind path still works meanwhile. The
 * banner surfaces the outbound + turn-hold backlog counts so nothing is hidden.
 *
 * Inline styles keep this free of NdaPage CSS-module coupling.
 */

const MNPI = 'mnpi_restricted';

const box: React.CSSProperties = {
  border: '1px solid var(--border, #d9d4c9)',
  borderRadius: 8,
  margin: '8px 0',
  background: 'var(--surface, #fff)',
};

export function NdaDecisionQueue() {
  const [open, setOpen] = useState(false);
  const summary = useNdaQueuesSummary();
  const candidates = useNdaExecutionCandidates();
  const intake = useNdaIntakeCandidates();

  const execCount = summary.data?.execution_candidates ?? 0;
  const outboundCount = summary.data?.outbound_candidates ?? 0;
  const turnHolds = summary.data?.turn_holds ?? 0;
  const intakeCount = summary.data?.intake_attach ?? 0;
  const total = execCount + outboundCount + turnHolds + intakeCount;

  // Hide until the count is known AND there is work — no empty flash while
  // loading, and no misleading empty banner if the summary GET errors.
  if (!summary.isSuccess || total === 0) return null;

  return (
    <div style={box}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            background: execCount + intakeCount > 0 ? '#b45309' : '#6b7280',
            color: '#fff',
            borderRadius: 12,
            padding: '1px 9px',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {execCount + intakeCount}
        </span>
        <span style={{ fontWeight: 600 }}>Decision queue</span>
        <span style={{ color: '#888', fontSize: 13 }}>
          {execCount} executed-copy confirm{execCount === 1 ? '' : 's'}
          {intakeCount > 0 && ` · ${intakeCount} intake to resolve`}
          {outboundCount > 0 && ` · ${outboundCount} outbound bind (use Maintenance)`}
          {turnHolds > 0 && ` · ${turnHolds} turn hold${turnHolds === 1 ? '' : 's'}`}
        </span>
        <span style={{ marginLeft: 'auto', color: '#888' }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ padding: '4px 14px 12px' }}>
          {candidates.isLoading && <div style={{ color: '#888' }}>Loading…</div>}
          {candidates.isError && (
            <div style={{ color: '#b91c1c' }}>Could not load the queue.</div>
          )}
          {candidates.data?.candidates.length === 0 && (
            <div style={{ color: '#888', fontSize: 13 }}>
              No executed-copy candidates awaiting confirmation.
            </div>
          )}
          {candidates.data?.candidates.map((c) => (
            <ExecutionCandidateCard key={c.work_item_id} candidate={c} />
          ))}

          {intakeCount > 0 && (
            <>
              <div style={{ fontWeight: 600, fontSize: 13, margin: '12px 0 2px' }}>
                Detected NDAs matching more than one matter
              </div>
              {intake.isLoading && <div style={{ color: '#888' }}>Loading…</div>}
              {intake.isError && (
                <div style={{ color: '#b91c1c' }}>Could not load intake candidates.</div>
              )}
              {intake.data?.candidates.map((c) => (
                <IntakeCandidateCard key={c.work_item_id} candidate={c} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IntakeCandidateCard({ candidate: c }: { candidate: NdaIntakeCandidate }) {
  const attach = useAttachIntakeCandidate();
  const createNew = useCreateNewIntakeCandidate();
  const busy = attach.isPending || createNew.isPending;

  return (
    <div
      style={{
        border: '1px solid var(--border, #e5e1d8)',
        borderRadius: 6,
        padding: '8px 10px',
        margin: '6px 0',
        display: 'grid',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{c.subject || 'detected NDA'}</strong>
        <span style={{ fontSize: 12, color: '#888' }}>matched via {c.via || 'document'}</span>
      </div>
      <div style={{ fontSize: 13, color: '#555' }}>
        Attach to the right matter, or create a new review if none fit:
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {c.candidates.map((opt) => (
          <button
            key={opt.review_id}
            type="button"
            disabled={busy}
            onClick={() => attach.mutate({ workItemId: c.work_item_id, review_id: opt.review_id })}
          >
            Attach → {opt.deal_name || `Review #${opt.review_id}`}
          </button>
        ))}
        <button type="button" disabled={busy} onClick={() => createNew.mutate(c.work_item_id)}>
          Create new review
        </button>
      </div>
      {(attach.isError || createNew.isError) && (
        <div style={{ color: '#b91c1c', fontSize: 12 }}>
          Action failed — it may have been resolved already, or the NDA-review budget
          is paused. Refresh the queue.
        </div>
      )}
    </div>
  );
}

function ExecutionCandidateCard({ candidate: c }: { candidate: NdaExecutionCandidate }) {
  const confirm = useConfirmExecutionCandidate();
  const dismiss = useDismissExecutionCandidate();
  const busy = confirm.isPending || dismiss.isPending;
  const isMnpi = c.source_permission_class === MNPI;

  const doConfirm = (createMatter: boolean) => {
    if (
      isMnpi &&
      !window.confirm(
        'This source is MNPI-restricted. Confirming captures the executed copy ' +
          'into the NDA record and IS the disclosure decision. Continue?',
      )
    ) {
      return;
    }
    confirm.mutate({
      workItemId: c.work_item_id,
      review_id: createMatter ? undefined : c.matched_review_id ?? undefined,
      create_matter: createMatter,
    });
  };

  return (
    <div
      style={{
        border: '1px solid var(--border, #e5e1d8)',
        borderRadius: 6,
        padding: '8px 10px',
        margin: '6px 0',
        display: 'grid',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{c.filename || 'executed copy'}</strong>
        <span style={{ fontSize: 12, color: '#888' }}>
          {c.cue_confidence === 'high' ? 'strong cue' : 'filename cue'}
          {c.cue_signals.length > 0 && ` · ${c.cue_signals.join(', ')}`}
        </span>
        {isMnpi && (
          <span
            style={{
              background: '#7f1d1d',
              color: '#fff',
              borderRadius: 4,
              padding: '0 6px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            MNPI
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#555' }}>
        {c.sender && <span>from {c.sender} · </span>}
        {c.subject}
      </div>
      <div style={{ fontSize: 13 }}>
        {c.matched_review_id ? (
          <span>
            matches <strong>{c.matched_review_label || `Review #${c.matched_review_id}`}</strong>
          </span>
        ) : (
          <span style={{ color: '#888' }}>no open matter matched</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {c.matched_review_id != null && (
          <button type="button" disabled={busy} onClick={() => doConfirm(false)}>
            Confirm → {c.matched_review_label || `Review #${c.matched_review_id}`}
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => doConfirm(true)}>
          Confirm as new matter
        </button>
        <button type="button" disabled={busy} onClick={() => dismiss.mutate(c.work_item_id)}>
          Dismiss
        </button>
      </div>

      {(confirm.isError || dismiss.isError) && (
        <div style={{ color: '#b91c1c', fontSize: 12 }}>
          Action failed — it may have been resolved already. Refresh the queue.
        </div>
      )}
    </div>
  );
}
